import { JobSectionVerb } from './job.section.rights';
import { PortalClientSecurityRight } from './portal.clients.security.module';

/** A tab of the Client Portal's job or site screen that has its own rights. */
export type PortalClientSection = 'jobTasks' | 'jobSors' | 'jobItems' | 'jobAppointments' | 'jobInvoices' | 'jobFiles' | 'jobActivities' | 'siteContacts' | 'siteServices' | 'siteQuotes' | 'siteJobs' | 'siteInvoices' | 'siteFiles' | 'siteActivities';

const R = PortalClientSecurityRight;

/**
 * The right behind each verb a client tab offers; a verb missing here is not offered (a View-only tab, or Delete on
 * Activities, which follows Edit). Read by the settings page and by the client section policy.
 */
export const PortalClientSectionRightIds: Record<PortalClientSection, Partial<Record<JobSectionVerb, PortalClientSecurityRight>>> = {
    jobTasks: { view: R.JobTasksView },
    jobSors: { view: R.JobSorsView },
    jobItems: { view: R.JobItemsView },
    jobAppointments: { view: R.JobAppointmentsView },
    jobInvoices: { view: R.ViewInvoicesInJob },
    jobFiles: { view: R.JobFilesView },
    jobActivities: { view: R.JobActivitiesView, create: R.JobActivitiesCreate, edit: R.JobActivitiesEdit },
    siteContacts: { view: R.SiteContactsView, create: R.SiteContactsCreate, edit: R.SiteContactsEdit, delete: R.SiteContactsDelete },
    siteServices: { view: R.SiteServicesView },
    siteQuotes: { view: R.ViewQuotesInSite },
    siteJobs: { view: R.ViewJobsInSite },
    siteInvoices: { view: R.ViewInvoicesInSite },
    siteFiles: { view: R.SiteFilesView },
    siteActivities: { view: R.SiteActivitiesView, create: R.SiteActivitiesCreate, edit: R.SiteActivitiesEdit },
};

/**
 * The new client tab rights a role needs on release day so its clients see what they saw before: the tabs that
 * were always shown get View (site contacts become view only: adding, changing and removing them is granted by an admin), Site Services follows Access Services Board, and the activity writes follow the
 * retired Create & Edit Activities. Job Tasks and Job SORs start off. Applied once per role — `Client User` in its
 * class, custom client roles by the tenant upgrade — and never re-derived afterwards. Job Invoices and Site Jobs /
 * Quotes / Invoices keep their ids, so they need nothing here.
 */
export function portalClientReleaseRights(rights: number[]): PortalClientSecurityRight[] {

    const always = [R.JobItemsView, R.JobAppointmentsView, R.JobFilesView, R.JobActivitiesView, R.SiteContactsView, R.SiteFilesView, R.SiteActivitiesView];
    const services = rights.includes(R.CanAccessServices) ? [R.SiteServicesView] : [];
    const activities = rights.includes(R.ManageActivity) ? [R.JobActivitiesCreate, R.JobActivitiesEdit, R.SiteActivitiesCreate, R.SiteActivitiesEdit] : [];

    return [...always, ...services, ...activities];
}

/** A system role's rights plus its release-day client tab rights, for the role classes. */
export function withPortalClientReleaseRights(rights: number[]): number[] {

    return [...new Set([...rights, ...portalClientReleaseRights(rights)])];
}
